import axios from 'axios';
import * as teams from "@microsoft/teams-js";
import {format} from "date-fns";
import { msalInstance } from '../services/MSALConfig';

const apiService = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  timeout: 40000
});

let xUserEmail = null;
let authToken = null;
export const setAuthToken = (token) => {
  authToken = token;
};

export const getApiToken = async () => {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error("No signed-in account found");
  let response = null;
  try {
    console.log("Silent SSO")
    response = await msalInstance.acquireTokenSilent({
      scopes: [process.env.REACT_APP_SCOPES],
      account: accounts[0],
    });
  } catch (err) {
    console.warn("Silent SSO failed, redirecting to login...", err);
    response = await msalInstance.loginRedirect({
      scopes: [process.env.REACT_APP_SCOPES],
    });
  }
  return response.accessToken;
};

export const getValidToken = async () => {
  try {
    // If Teams token already exists, use it
    if (authToken) return authToken;
    // Try web (MSAL)
    return await getApiToken();
  } catch (err) {
    // If MSAL fails, fallback to Teams
    return await obtainAuthToken();
  }
};

apiService.interceptors.request.use(async (config) => {
  const token = await getValidToken();

  config.headers.Authorization = `Bearer ${token}`;

  if (process.env.REACT_APP_ENVIRONMENT === 'local') {
    config.headers['X-User-Email'] = 'deni.lanc@a23.com.au';
    config.headers['X-Authorization'] = `Bearer ${token}`;
  }

  if (xUserEmail !== null) {
    config.headers['X-User-Email'] = xUserEmail;
  }

  return config;
});

export const changeUser = async (userEmail) => {
  xUserEmail = userEmail;
}

apiService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      authToken = null;
      try {
        const newToken = await getValidToken();

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiService(originalRequest);
        }
      } catch (err) {
        console.error("Token refresh failed", err);
      }
    }

    return Promise.reject(error);
  }
);

export const obtainAuthToken = async () => {
  try {
    await teams.app.initialize();
    const token = await teams.authentication.getAuthToken();
    setAuthToken(token);
    return token;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    throw error;
  }
};


export const refreshAuthToken = async () => {
  return null;
}


export const getClientProjectTimecodeForUser = async (weekStartDate) => {
  const response = await apiService.get('/GetA23ResourceAssignments', {
      params: {
          weekstartdate: format(weekStartDate, "ddMMyyyy").toString()
      }
    });
  return response.data;
};

export const SetTimeEntryDetails = async (taskId, date, timeEntryDetail, toDelete) => {

  const response = await apiService.post('/SetTimeEntryDetails', {
    taskId,
    date,
    timeEntryDetail,
    toDelete
  });
  return response.data;
};

export const GetA23WeekTimeEntries = async (weekStartDate) => {
  const response = await apiService.get('/GetA23WeekTimeEntries', {
        params: {
          weekstartdate: format(weekStartDate, "ddMMyyyy").toString()
        }
      });
  return response.data;
}

export const GetTimesheetData = async (weekStartDate) => {
  const response = await apiService.get('/GetA23TimesheetData', {
      params: {
        weekstartdate: format(weekStartDate, "ddMMyyyy").toString()
      }
    });
  return response.data;
};

export const SubmitTimesheet = async (weekStartDate) => {
  const response = await apiService.get('/SubmitTimesheet', {
      params: {
        weekstartdate: format(weekStartDate, "ddMMyyyy").toString()
      }
    });
  return response.data;
};

export const TestDebugWriter = async (weekStartDate) => {
  const response = await apiService.get('/TestDebugWriter', {
      params: {
        // weekstartdate: format(weekStartDate, "ddMMyyyy")
      }
    });
  return response.data;
}

export const GetA23ExternalApproval = async (monthStartDate) => {
  const response = await apiService.get('/GetA23ExternalApproval', {
      params: {
        monthStartDate: format(monthStartDate, "ddMMyyyy").toString()
      }
    });
  return response.data;
};

export const SendTimesheetReportToClient = async (approverEmail, monthStartDate) => {
  const response = await apiService.get('/SendTimesheetReportToClient', {
      params: {
        approverEmail: approverEmail,
        monthStartDate: format(monthStartDate, "ddMMyyyy").toString()
      },
      timeout: 60000
    });
  return response.data;
};

export const GetA23UnsubmittedUsers = async (weekstartdate) => {
  const response = await apiService.get('/GetA23UnsubmittedUsers', {
      params: {
        weekstartdate: format(weekstartdate, "ddMMyyyy").toString()
      }
    });
  return response.data;
};

export const SyncResourceAssignments = async () => {
  const response = await apiService.get('/SyncResourceAssignments');
  return response.data;
};

export const SyncUsers = async () => {
  const response = await apiService.get('/SyncUsers');
  return response.data;
};

export const SyncTimesheetStatus = async () => {
  const response = await apiService.get('/SyncTimesheetStatus');
  return response.data;
};

export const SyncLeave = async () => {
  const response = await apiService.get('/SyncLeave')
  return response.data;
}

export const SyncHolidays = async () => {
  const response = await apiService.get('/SyncHolidays')
  return response.data;
}

export const SyncLocations = async () => {
  const response = await apiService.get('/SyncLocations')
  return response.data;
}

export const SubmitCasualMonthly = async (weekStartDate) => {
  const response = await apiService.get('/CasualMonthlySubmit', {
      params: {
        weekstartdate: format(weekStartDate, "ddMMyyyy").toString()
      },
      timeout: 50000
    });
  return response.data;
};

export const TimesheetExport = async (email, date, type) => {
  const response = await apiService.get('/TimesheetExport', {
      params: {
        email: email,
        weekstartdate: format(date, "ddMMyyyy").toString(),
        type: type
      },
      timeout: 60000,
      responseType: 'blob'
    });
  return response.data;
};

export const GetA23MonthlyEntries = async (weekStartDate) => {
  const response = await apiService.get('/GetA23MonthlyEntries', {
      params: {
        weekstartdate: format(weekStartDate, "ddMMyyyy").toString()
      }
    });
  return response.data;
};

export const ManualDocusignApproval = async (userId, weekStartDate, approverEmail) => {
  const response = await apiService.get('/ManualDocusignApproval', {
      params: {
        userid: userId,
        weekstartdate: format(weekStartDate, "ddMMyyyy").toString(),
        approveremail: approverEmail
      }
    });
  return response.data;
};

export const GetA23MonthlySubmissionStatus = async (monthStartDate) => {
  const response = await apiService.get('/GetA23MonthlySubmissionStatus', {
      params: {
        monthStartDate: format(monthStartDate, "ddMMyyyy").toString()
      }
    });
  return response.data;
};

export const UnsubmitMonthlySubmission = async (userid, date) => {
  const response = await apiService.get('/UnsubmitMonthlySubmission', {
      params: {
        userid: userid,
        monthStartDate: format(date, "ddMMyyyy").toString(),
      },
    });
  return response.data;
};

export const GetHolidayCalendar = async ( weekStartDate) =>{
    const response = await apiService.get('/GetA23HolidayCalendar', {
      params: {
        weekstartdate: format(weekStartDate, "ddMMyyyy").toString()
      },
    });
  return response.data;
}

export const GetA23Users = async () => {
  const response = await apiService.get('/GetA23Users', {
      params: {}
    });
  return response.data;
};